import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Stack,
  Avatar,
  Chip,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { FaFileExcel, FaFilePdf, FaSync, FaSearch } from "react-icons/fa";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import generateAdmissionForm from "./generateAdmissionForm";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import api from "../../api";

const ViewStudent = () => {
  const navigate = useNavigate();
  const schoolId = localStorage.getItem("school_id");
  const token = localStorage.getItem("token");

  const [students, setStudents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [classList, setClassList] = useState([]);
  const [sections, setSections] = useState([]);
  const [rolls, setRolls] = useState([]);
  const [filters, setFilters] = useState({
    session: null,
    class: null,
    section: null,
    roll: null,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const [presentModalOpen, setPresentModalOpen] = useState(false);
  const [presentStudent, setPresentStudent] = useState(null);
  const [presentDayValue, setPresentDayValue] = useState("");
  const [savingPresentDay, setSavingPresentDay] = useState(false);

  // --------------------------
  //  AUTH CHECK
  // --------------------------
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
  }, [navigate]);

  // 🧠 Auth check
  const ensureAuth = () => {
    if (!token) {
      window.location.href = "/login";
      return false;
    }
    return true;
  };

  // 🧩 Fetch Sessions
  const fetchSessions = async () => {
    try {
      const res = await api.get("school/academic_year/list", {
        headers: { "X-School-Id": schoolId },
      });

      if (res.data?.status !== false) {
        const data = res.data.data || [];
        data.sort((a, b) => (a.status === "active" ? -1 : 1));
        setSessions(data);

        // Auto-select active session
        if (!filters.session && data.length) {
          const active = data.find((s) => s.status === "active");
          setFilters((p) => ({ ...p, session: active || data[0] }));
        }
      }
    } catch {
      Swal.fire("Error", "Failed to fetch sessions", "error");
    }
  };

  // 🧩 Fetch Classes
  const fetchClasses = async () => {
    try {
      const res = await api.get("/get-classes", {
        headers: { "X-School-Id": schoolId },
      });
      if (res.data?.success) setClassList(res.data.data || []);
    } catch {
      setClassList([]);
    }
  };

  // 🧩 Fetch Sections on class change
  useEffect(() => {
    if (!filters.class) {
      setSections([]);
      setFilters((p) => ({ ...p, section: null, roll: null }));
      return;
    }
    const fetchSections = async () => {
      try {
        const res = await api.get(`/get-sections/${filters.class.id}`, {
          headers: { "X-School-Id": schoolId },
        });
        if (res.data?.success) setSections(res.data.data || []);
      } catch {
        setSections([]);
      }
    };
    fetchSections();
  }, [filters.class]);

  // 🧩 Fetch Rolls on section change
  useEffect(() => {
    if (!filters.section) {
      setRolls([]);
      setFilters((p) => ({ ...p, roll: null }));
      return;
    }

    const fetchRolls = async () => {
      try {
        const res = await api.get(`/get-rolls/${filters.section.id}`, {
          headers: { "X-School-Id": schoolId },
        });
        if (res.data?.success) {
          const rollNumbers = res.data.data
            .map((r) => parseInt(r.roll_no))
            .filter((n) => !isNaN(n));
          setRolls([...new Set(rollNumbers)].sort((a, b) => a - b));
        } else setRolls([]);
      } catch {
        setRolls([]);
      }
    };
    fetchRolls();
  }, [filters.section]);

  // 🧩 Init
  useEffect(() => {
    if (!ensureAuth()) return;
    fetchSessions();
    fetchClasses();
  }, []);

  // 🧩 Fetch All Students (no pagination)
  const fetchStudents = async () => {
    if (!ensureAuth()) return;
    setLoading(true);
    try {
      const params = { page: 1, limit: 0 }; // ✅ Fetch all
      if (filters.class) params.class = filters.class.id;
      if (filters.section) params.section = filters.section.id;
      if (filters.roll) params.roll = filters.roll;
      if (filters.session) params.session_id = filters.session.id;

      const res = await api.get("/view-student", {
        params,
        headers: { "X-School-Id": schoolId },
      });

      const data = res.data;
      if (data.status && Array.isArray(data.students)) {
        setStudents(data.students);
      } else {
        setStudents([]);
      }
    } catch {
      Swal.fire("Error", "Unable to fetch students", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🧩 Auto fetch when filter changes
  // useEffect(() => {
  //   if (filters.session) fetchStudents();
  // }, [filters.session, filters.class, filters.section, filters.roll]);

  // 🔍 Search filter
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return students;
    const q = searchQuery.toLowerCase();
    return students.filter(
      (s) =>
        (s.name && s.name.toLowerCase().includes(q)) ||
        (s.mobile && s.mobile.toString().includes(q)) ||
        (s.student_code && s.student_code.toLowerCase().includes(q))
    );
  }, [searchQuery, students]);

  // 🔹 Helper: Date format
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date)) return "";
    return `${String(date.getDate()).padStart(2, "0")}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${date.getFullYear()}`;
  };

  // 🔹 Export Excel
  const exportToExcel = () => {
    const formatted = filteredData.map((r, i) => ({
      "#": i + 1,
      Name: r.name,
      Code: r.student_code,
      DOB: formatDate(r.dob),
      Mobile: r.mobile,
      Class: r.class_name,
      Section: r.section_name,
      Roll: r.roll_no,
      Gender: r.gender,
      "Father's Name": r.father_name || "",
      "Admission Date": formatDate(r.created_at),
    }));
    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Students");
    XLSX.writeFile(wb, "Students.xlsx");
  };

  // 🔹 Export PDF
  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const head = [
      [
        "#",
        "Name",
        "Code",
        "DOB",
        "Mobile",
        "Class",
        "Section",
        "Roll",
        "Gender",
        "Father's Name",
        "Admission Date",
      ],
    ];
    const body = filteredData.map((r, i) => [
      i + 1,
      r.name,
      r.student_code,
      formatDate(r.dob),
      r.mobile,
      r.class_name,
      r.section_name,
      r.roll_no,
      r.gender,
      r.father_name || "",
      formatDate(r.created_at),
    ]);
    doc.autoTable({ head, body, startY: 15 });
    doc.save("Students.pdf");
  };

  const handleGeneratePdf = async (studentId) => {
    try {
      if (!studentId) {
        Swal.fire("Error", "Invalid Student ID", "error");
        return;
      }

      Swal.fire({
        title: "Generating PDF...",
        text: "Please wait",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const res = await api.post(`/student_data/${studentId}`);

      if (res.data?.status) {
        const detail = res.data.data;
        await generateAdmissionForm(detail);

        Swal.close();
        Swal.fire("Success!", "PDF generated successfully!", "success");
      } else {
        Swal.fire("Error", "Failed to fetch student details", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  const handlePrintAdmissionForm = async (studentId) => {
    try {
      if (!studentId) {
        Swal.fire("Error", "Invalid Student ID", "error");
        return;
      }

      Swal.fire({
        title: "Preparing Admission Form...",
        text: "Please wait",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const res = await api.post(`/student_data/${studentId}`);
      Swal.close();

      if (!res?.data?.status || !res.data.data) {
        Swal.fire("Error", "Failed to fetch student details", "error");
        return;
      }

      const data = res.data.data;

      const school = Array.isArray(data.school) ? data.school[0] || {} : {};
      const address = data.address || {};
      const bank = data.bank || {};
      const fees = Array.isArray(data.fees) ? data.fees : [];

      const logoUrl = school.logo
        ? `https://dgps.co.in/public/admin/assets/img/schools/logo/${school.logo}`
        : "https://dgps.co.in/public/admin/assets/img/logo.png";

      const photoUrl =
        data.file ||
        "https://dgps.co.in/public/admin/assets/img/default-stu.png";

      const totalAmount = fees.reduce(
        (sum, f) => sum + (parseFloat(f.amount || 0) || 0),
        0
      );

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        Swal.fire(
          "Popup Blocked",
          "Please allow pop-ups for this site to print the form.",
          "warning"
        );
        return;
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8" />
          <title>Student Admission Form</title>
          <style>
            * { box-sizing: border-box; }

            body {
              font-family: "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
              background: #eef2f7;
              margin: 0;
              padding: 20px;
            }

            .page-frame {
              max-width: 820px;
              margin: 0 auto;
              background: #f5f8ff;
              border-radius: 14px;
              padding: 10px;
              border: 3px solid #0050a8;
            }

            .page-inner {
              border-radius: 10px;
              border: 2px solid #0050a8;
              background: #ffffff;
              padding: 22px 26px 26px;
            }

            /* ------------ HEADER ------------ */
            .header {
              display: flex;
              align-items: center;
              gap: 18px;
              margin-bottom: 10px;
            }

            .school-logo {
              width: 90px;
              height: 90px;
              object-fit: contain;
            }

            .school-text {
              flex: 1;
              text-align: center;
            }

            .school-name {
              font-size: 22px;
              font-weight: 700;
              letter-spacing: 0.6px;
              color: #00408a;
              text-transform: uppercase;
            }

            .school-address {
              font-size: 12px;
              color: #444;
              margin-top: 3px;
            }

            .school-contact {
              font-size: 11px;
              color: #555;
              margin-top: 2px;
            }

            .top-divider {
              margin-top: 10px;
              border-bottom: 2px solid #0050a8;
            }

            /* ------------ TITLE ------------ */
            .title-bar {
              margin: 18px auto 18px;
              max-width: 420px;
              background: linear-gradient(90deg, #00428f, #0070e0);
              color: #ffffff;
              text-align: center;
              padding: 8px 18px;
              border-radius: 999px;
              font-weight: 600;
              font-size: 14px;
              letter-spacing: 0.5px;
              text-transform: uppercase;
              box-shadow: 0 3px 8px rgba(0, 64, 138, 0.25);
            }

            /* ------------ MAIN LAYOUT (Option A) ------------ */
            .layout-main {
              display: flex;
              align-items: flex-start;
              gap: 18px;
              margin-top: 4px;
            }

            .left-column {
              flex: 1.6;
              min-width: 0;
            }

            

            /* ------------ CARDS ------------ */
            .section-heading {
              font-size: 13px;
              font-weight: 600;
              color: #00408a;
              border-bottom: 1px solid #d0d8ea;
              padding-bottom: 4px;
              margin-bottom: 6px;
            }

            .card-soft {
              border-radius: 10px;
              border: 1px solid #d3e0f5;
              background: #f9fbff;
              box-shadow: 0 2px 6px rgba(0,0,0,0.03);
              padding: 12px 14px 10px;
              margin-bottom: 12px;
              font-size: 12px;
            }

            .card {
              border-radius: 10px;
              border: 1px solid #d3e0f5;
              background: #ffffff;
              box-shadow: 0 1px 4px rgba(0,0,0,0.03);
              padding: 10px 14px 8px;
              margin-bottom: 12px;
              font-size: 12px;
            }

            .student-grid {
              display: grid;
              grid-template-columns: 1.1fr 1.1fr;
              column-gap: 40px;
              row-gap: 4px;
              font-size: 12px;
            }

            .field-row {
              display: flex;
              margin: 2px 0;
            }

            .field-label {
              width: 95px;
              font-weight: 600;
              color: #222;
            }

            .field-value {
              flex: 1;
              color: #333;
            }

            .card-grid-two {
              display: grid;
              grid-template-columns: 1.1fr 1.1fr;
              column-gap: 40px;
            }

            .card-row {
              display: flex;
              margin: 3px 0;
            }

            .card-label {
              width: 95px;
              font-weight: 600;
              color: #222;
            }

            .card-value {
              flex: 1;
              color: #333;
            }

            /* ------------ PHOTO PANEL (separate right column) ------------ */
            .photo-panel {
              border-radius: 16px;
              border: 1px solid #d3e0f5;
              background: radial-gradient(circle at 10% 0%, #e8f4ff 0%, #c8e5ff 45%, #f5fbff 100%);
              box-shadow: 0 4px 12px rgba(0,0,0,0.12);
              padding: 10px;
              text-align: center;
            }

            .photo-title {
              font-size: 11px;
              font-weight: 600;
              color: #00408a;
              margin-bottom: 6px;
              letter-spacing: 0.4px;
              text-transform: uppercase;
            }

            .photo-box {
              border-radius: 12px;
              overflow: hidden;
              background: #e6f6ff;
              border: 2px solid #00a0e3;
            }

            .photo-img {
              width: 100px;
              object-fit: cover;
            }

            /* ------------ FEES TABLE ------------ */
            .fees-table-wrapper {
              border-radius: 10px;
              border: 1px solid #d3e0f5;
              overflow: hidden;
              box-shadow: 0 1px 4px rgba(0,0,0,0.04);
            }

            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 12px;
            }

            thead {
              background: linear-gradient(90deg, #00428f, #0070e0);
              color: #ffffff;
            }

            th, td {
              padding: 8px 10px;
              text-align: left;
            }

            th {
              font-weight: 600;
              font-size: 12px;
            }

            tbody tr:nth-child(even) {
              background: #f4f7ff;
            }

            tbody tr:nth-child(odd) {
              background: #ffffff;
            }

            .amount-cell {
              text-align: right;
            }

            .total-row {
              background: #dbe7ff !important;
              font-weight: 700;
              border-top: 1px solid #b4c7f2;
            }

            @media print {
              body {
                background: #ffffff;
                padding: 0;
              }
              .page-frame {
                border-radius: 0;
                border-width: 2px;
              }
              .page-inner {
                box-shadow: none;
                border-radius: 0;
              }
              .layout-main {
                gap: 14px;
              }
            }
          </style>
        </head>
        <body>
          <div class="page-frame">
            <div class="page-inner">

              <!-- HEADER -->
              <div class="header">
                <img src="${logoUrl}" class="school-logo" />
                <div class="school-text">
                  <div class="school-name">${school.name || ""}</div>
                  <div class="school-address">${school.address || ""}</div>
                  <div class="school-contact">
                    ${school.phone ? "Phone: " + school.phone : ""}
                    ${school.phone && school.email ? " | " : ""}
                    ${school.email ? "Email: " + school.email : ""}
                  </div>
                </div>
              </div>
              <div class="top-divider"></div>

              <!-- TITLE -->
              <div class="title-bar">Student Admission Form</div>

              <!-- MAIN LAYOUT (LEFT: DETAILS, RIGHT: PHOTO) -->
              <div class="layout-main">

                <!-- LEFT COLUMN -->
                <div class="left-column">
                  <!-- STUDENT INFO -->
                  <div class="card-soft">
                    <div class="section-heading">Student Information</div>
                    <div class="student-grid">
                      <div>
                        <div class="field-row">
                          <div class="field-label">Name:</div>
                          <div class="field-value">${data.name || "-"}</div>
                        </div>
                        <div class="field-row">
                          <div class="field-label">Father's Name:</div>
                          <div class="field-value">${data.father_name || "-"}</div>
                        </div>
                        <div class="field-row">
                          <div class="field-label">Mother's Name:</div>
                          <div class="field-value">${data.mother_name || "-"}</div>
                        </div>
                        <div class="field-row">
                          <div class="field-label">Gender:</div>
                          <div class="field-value">${data.gender || "-"}</div>
                        </div>
                      </div>
                      <div>
                        <div class="field-row">
                          <div class="field-label">Date of Birth:</div>
                          <div class="field-value">${data.dob || "-"}</div>
                        </div>
                        <div class="field-row">
                          <div class="field-label">Aadhaar No:</div>
                          <div class="field-value">${data.aadhaar_no || "-"}</div>
                        </div>
                        <div class="field-row">
                          <div class="field-label">Blood Group:</div>
                          <div class="field-value">${data.blood_group || "-"}</div>
                        </div>
                        <div class="field-row">
                          <div class="field-label">Mobile:</div>
                          <div class="field-value">${data.mobile || "-"}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- ADDRESS -->
                  <div class="card">
                    <div class="section-heading">Address</div>
                    <div class="card-grid-two">
                      <div>
                        <div class="card-row">
                          <div class="card-label">Village:</div>
                          <div class="card-value">${address.village || "-"}</div>
                        </div>
                        <div class="card-row">
                          <div class="card-label">Post Office:</div>
                          <div class="card-value">${address.post_office || "-"}</div>
                        </div>
                        <div class="card-row">
                          <div class="card-label">Police Station:</div>
                          <div class="card-value">${address.police_station || "-"}</div>
                        </div>
                        <div class="card-row">
                          <div class="card-label">Pin Code:</div>
                          <div class="card-value">${address.pincode || "-"}</div>
                        </div>
                      </div>
                      <div>
                        <div class="card-row">
                          <div class="card-label">District:</div>
                          <div class="card-value">${address.city || "-"}</div>
                        </div>
                        <div class="card-row">
                          <div class="card-label">State:</div>
                          <div class="card-value">${address.state || "-"}</div>
                        </div>
                        <div class="card-row">
                          <div class="card-label">Block:</div>
                          <div class="card-value">${address.block || "-"}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- ACADEMIC -->
                  <div class="card">
                    <div class="section-heading">Academic Details</div>
                    <div class="card-grid-two">
                      <div>
                        <div class="card-row">
                          <div class="card-label">Class:</div>
                          <div class="card-value">${data.class_name || "-"}</div>
                        </div>
                        <div class="card-row">
                          <div class="card-label">Section:</div>
                          <div class="card-value">${data.section_name || "-"}</div>
                        </div>
                      </div>
                      <div>
                        <div class="card-row">
                          <div class="card-label">Roll No:</div>
                          <div class="card-value">${data.roll_no || "-"}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- FEES -->
                  <div class="card">
                    <div class="section-heading">Fees Details</div>
                    <div class="fees-table-wrapper">
                      <table>
                        <thead>
                          <tr>
                            <th>Fee Name</th>
                            <th class="amount-cell">Amount (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          ${fees.length
          ? fees
            .map(
              (f) => `
                            <tr>
                              <td>${f.fee_name || "-"}</td>
                              <td class="amount-cell">${(
                  parseFloat(f.amount || 0) || 0
                ).toFixed(2)}</td>
                            </tr>`
            )
            .join("")
          : `<tr><td colspan="2">No fees configured</td></tr>`
        }
                          <tr class="total-row">
                            <td>Total</td>
                            <td class="amount-cell">${totalAmount.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <!-- RIGHT COLUMN (PHOTO ONLY) -->
                <div class="right-column">
                  <div class="photo-panel">
                    <div class="photo-title">Student Photo</div>
                    <div class="photo-box">
                      <img src="${photoUrl}" class="photo-img" />
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <script>
            window.onload = function () {
              window.print();
            };
          </script>
        </body>
        </html>
    `);

      printWindow.document.close();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Something went wrong while generating form", "error");
    }
  };

  const openPresentDayModal = (row) => {
    setPresentStudent(row);
    setPresentDayValue(row.present_day || "");
    setPresentModalOpen(true);
  };

  const handleSavePresentDay = async () => {
    if (!presentDayValue) {
      toast.warning("Please enter present day");
      return;
    }

    setSavingPresentDay(true);

    try {
      const res = await api.post(
        `/add-present_day/${presentStudent.id}`,
        {
          present_day: presentDayValue,
        }
      );

      if (res.data?.status) {
        toast.success("Present day updated");
        setPresentModalOpen(false);
        fetchStudents(); // 🔁 list refresh (important)
      } else {
        toast.error(res.data?.message || "Failed");
      }
    } catch (err) {
      toast.error("Server error");
    } finally {
      setSavingPresentDay(false);
    }
  };

  // Columns
  const columns = useMemo(
    () => [
      {
        name: "Photo",
        cell: (row) => (
          <Avatar
            src={row.file || "/default-user.png"}
            alt={row.name}
            sx={{ width: 40, height: 40 }}
          />
        ),
        width: "80px",
      },
      { name: "Name", selector: (row) => row.name, sortable: true },
      { name: "Code", selector: (row) => row.student_code, sortable: true },
      { name: "DOB", selector: (row) => formatDate(row.dob), sortable: true },
      { name: "Mobile", selector: (row) => row.mobile, sortable: true },
      { name: "Class", selector: (row) => row.class_name, sortable: true },
      { name: "Section", selector: (row) => row.section_name, sortable: true },
      { name: "Roll", selector: (row) => row.roll_no, sortable: true },
      {
        name: "Gender",
        cell: (row) => (
          <Chip
            label={row.gender || "-"}
            color={row.gender === "Male" ? "primary" : "secondary"}
            size="small"
          />
        ),
      },
      { name: "Father's Name", selector: (row) => row.father_name || "-" },
      {
        name: "Present Day",
        center: true,
        cell: (row) => (
          <Stack direction="row" spacing={1} alignItems="center">
            {row.present_day ? (
              <>
                <Chip
                  label={row.present_day}
                  color="success"
                  size="small"
                />

                <Tooltip title="Update Present Day">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => openPresentDayModal(row)}
                  >
                    ✏️
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={() => openPresentDayModal(row)}
              >
                Add
              </Button>
            )}
          </Stack>
        ),
      },


      {
        name: "Action",
        center: true,
        cell: (row) => (
          <Stack direction="row" spacing={1}>

            {/* VIEW */}
            <Tooltip title="View Student">
              <IconButton
                size="small"
                color="primary"
                onClick={() =>
                (window.location.href = `/admin/view-student-details/${btoa(
                  String(row.student_id || row.id)
                )}`)
                }
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* EDIT */}
            <Tooltip title="Edit Student">
              <IconButton
                size="small"
                color="warning"
                onClick={() =>
                (window.location.href = `/admin/edit-student/${btoa(
                  String(row.id)
                )}`)
                }
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* PRINT */}
            <Tooltip title="Print Admission Form">
              <IconButton
                size="small"
                color="success"
                onClick={() =>
                  handlePrintAdmissionForm(row.id)
                }
              >
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>

          </Stack>
        ),
      }


    ],
    []
  );



  return (
    <>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" fontWeight="bold" mb={2}>
          🎓 Student Records
        </Typography>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={sessions}
                getOptionLabel={(opt) =>
                  `${opt.session_name || ""}${opt.status ? ` (${opt.status})` : ""}`
                }
                value={filters.session}
                onChange={(_, v) => setFilters((p) => ({ ...p, session: v }))}
                renderInput={(params) => <TextField {...params} label="Session" />}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={classList}
                getOptionLabel={(opt) => opt.class_name || opt.name || ""}
                value={filters.class}
                onChange={(_, v) => setFilters((p) => ({ ...p, class: v }))}
                renderInput={(params) => <TextField {...params} label="Class" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={sections}
                getOptionLabel={(opt) => opt.section_name || opt.name || ""}
                value={filters.section}
                onChange={(_, v) => setFilters((p) => ({ ...p, section: v }))}
                renderInput={(params) => <TextField {...params} label="Section" />}
                disabled={!filters.class}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={rolls || []}
                value={filters.roll || null}
                onChange={(_, v) => setFilters((p) => ({ ...p, roll: v }))}
                getOptionLabel={(opt) => opt?.toString() || ""}
                renderInput={(params) => <TextField {...params} label="Roll" />}
                disabled={!filters.section}
              />
            </Grid>
            <Grid item xs={12} md={3} sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                onClick={fetchStudents}
                startIcon={<FaSearch />}
                sx={{
                  height: "56px",
                  px: 4,
                  borderRadius: "30px",
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 500,
                  background: "linear-gradient(90deg, #6a6ff5, #7b7ffb)",
                  boxShadow: "0 8px 20px rgba(106,111,245,0.35)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #5a5fe0, #6b6ff0)",
                    boxShadow: "0 10px 24px rgba(106,111,245,0.45)",
                  },
                }}
              >
                Search Student
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography variant="h6">
              📋 Student List ({filteredData.length})
            </Typography>

            <Stack direction="row" spacing={1}>
              <TextField
                placeholder="Search by name, mobile or ID..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: "250px" }}
              />
              <Button
                variant="contained"
                color="success"
                startIcon={<FaFileExcel />}
                onClick={exportToExcel}
              >
                Excel
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<FaFilePdf />}
                onClick={exportToPDF}
              >
                PDF
              </Button>
            </Stack>
          </Stack>

          {loading ? (
            <Box textAlign="center" py={5}>
              <CircularProgress />
              <Typography mt={2}>Loading Students...</Typography>
            </Box>
          ) : (
            <DataTable
              columns={columns}
              data={filteredData}
              highlightOnHover
              responsive
              pagination={false} // 🚫 pagination removed
              dense
              customStyles={{
                headCells: {
                  style: { fontWeight: "bold", backgroundColor: "#f5f6f8" },
                },
                rows: { style: { minHeight: "65px" } },
              }}
            />
          )}
        </Paper>
      </Box>

      {/* ✅ PRESENT DAY MODAL (ALWAYS OUTSIDE TABLE, BUT INSIDE RETURN) */}
      <Dialog
        open={presentModalOpen}
        onClose={() => setPresentModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {presentStudent?.present_day ? "Update Present Day" : "Add Present Day"}
        </DialogTitle>


        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label="Present Day"
            value={presentDayValue}
            onChange={(e) => setPresentDayValue(e.target.value)}
            margin="dense"
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setPresentModalOpen(false)}
            disabled={savingPresentDay}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePresentDay}
            variant="contained"
            disabled={savingPresentDay}
          >
            {savingPresentDay
              ? "Saving..."
              : presentStudent?.present_day
                ? "Update"
                : "Save"}
          </Button>

        </DialogActions>
      </Dialog>
    </>
  );

};

export default ViewStudent;
